import React from "react";
import { Box, Typography, Chip, Button } from "@mui/material";
import { Iconify } from "@/components/iconify/iconify";
import { useReporting } from "@/hooks/useReporting";

export const CriteriaResultItem = ({ result, index, onEventGenerated }) => {
  const { logEvent } = useReporting();

  const handleCheatClick = async () => {
    try {
      // Generate metadata based on the criteria properties
      const metadata = {};

      // If there are props, try to match them in metadata
      if (result.props && result.props.length > 0) {
        result.props.forEach((prop) => {
          // Convert property conditions to metadata
          metadata[prop.propertyName] = prop.propertyValue;
        });
      }

      // Generate a whatId if the event type suggests it needs one
      let whatId = null;
      if (result.eventName.includes("HAT")) {
        whatId = "HAT-example-hat-id";
      } else if (result.eventName.includes("LADDER")) {
        whatId = "LADDER-example-ladder-id";
      } else if (result.eventName.includes("CLIMB")) {
        whatId = "CLIMB-example-climb-id";
      } else if (result.eventName.includes("ACTIVITY")) {
        whatId = "ACTIVITY-example-activity-id";
      } else if (result.eventName.includes("CONTENT")) {
        whatId = "CONTENT-example-content-id";
      } else if (result.eventName.includes("RESOURCE")) {
        whatId = "RESOURCE-example-resource-id";
      } else if (result.eventName.includes("VIDEO")) {
        whatId = "VIDEO-example-video-id";
      } else if (result.eventName.includes("PATHWAY")) {
        whatId = "PATHWAY-example-pathway-id";
      }

      // Call logEvent with appropriate parameters
      await logEvent.mutateAsync({
        whatId,
        eventType: result.eventName,
        result: "success",
        score: metadata.score ? parseInt(metadata.score) : null,
        maxScore: metadata.maxScore ? parseInt(metadata.maxScore) : null,
        metadata: {
          ...metadata,
          cheat: true,
        },
      });

      // Notify parent component that an event was generated
      if (onEventGenerated) {
        onEventGenerated(result.eventName);
      }

      console.log(`Generated cheat event for: ${result.eventName}`, {
        whatId,
        eventType: result.eventName,
        metadata,
      });
    } catch (error) {
      console.error("Failed to generate cheat event:", error);
    }
  };

  const needsMoreEvents = result.matchingCount < result.requiredCount;

  return (
    <Box
      key={result.criterionId || index}
      sx={{ mb: 1, p: 1, bgcolor: "grey.50", borderRadius: 1 }}
      label="Criteria result item"
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Typography variant="body2" fontWeight="bold">
          {result.eventName}
        </Typography>
        <Chip
          label={result.satisfied ? "Met" : "Not Met"}
          color={result.satisfied ? "success" : "warning"}
          size="small"
          variant="outlined"
        />
        {needsMoreEvents && (
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={handleCheatClick}
            startIcon={<Iconify icon="eva:flash-fill" />}
            sx={{ ml: "auto" }}
            label="Generate cheat event"
          >
            Cheat
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Required: {result.requiredCount} | Found: {result.matchingCount}
      </Typography>

      {result.propsCompact && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic" }}
        >
          Conditions: {result.propsCompact}
        </Typography>
      )}

      {result.props && result.props.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {result.props.map((prop, propIndex) => (
            <Chip
              key={propIndex}
              label={`${prop.propertyName} ${prop.comparisonType} ${prop.propertyValue}`}
              size="small"
              variant="outlined"
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </Box>
      )}

      {result.matchingEventIds && result.matchingEventIds.length > 0 && (
        <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
          Matching events: {result.matchingEventIds.length}
        </Typography>
      )}
    </Box>
  );
};
